// Character images per gamification level (1..30), per gender.
//
// Male frames live in `./character/level-NN.webp`, female frames in
// `./character/female/level-NN.webp`. We import every entry via
// `import.meta.glob` with `?url` so each becomes a separate Vite chunk:
// the JS bundle only ships URLs, the actual PNG is fetched lazily by the
// browser when the <img src=...> renders. Keeps initial load light even
// though the full character set is large.
//
// `getCharacterImage(level, gender)` clamps to the valid 1..30 range and
// falls back to the closest available level. If a female frame is missing
// it falls back to the male frame of the same level so the UI never breaks.

const maleModules = import.meta.glob('./character/level-*.webp', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>

const femaleModules = import.meta.glob('./character/female/level-*.webp', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>

function buildByLevel(modules: Record<string, string>): Record<number, string> {
  const byLevel: Record<number, string> = {}
  for (const [path, url] of Object.entries(modules)) {
    const match = path.match(/level-(\d+)\.webp$/)
    if (match) byLevel[parseInt(match[1], 10)] = url
  }
  return byLevel
}

const byLevelMale = buildByLevel(maleModules)
const byLevelFemale = buildByLevel(femaleModules)

export const MIN_CHARACTER_LEVEL = 1
export const MAX_CHARACTER_LEVEL = 30

export type CharacterGender = 'MALE' | 'FEMALE' | null | undefined

function pickFromMap(byLevel: Record<number, string>, clamped: number): string | null {
  // Exact match if present; otherwise pick the highest available level ≤ clamped
  if (byLevel[clamped]) return byLevel[clamped]
  for (let l = clamped - 1; l >= MIN_CHARACTER_LEVEL; l--) {
    if (byLevel[l]) return byLevel[l]
  }
  return null
}

export function getCharacterImage(
  level: number | null | undefined,
  gender?: CharacterGender,
): string | null {
  const female = gender === 'FEMALE'
  const primary = female ? byLevelFemale : byLevelMale
  const fallback = female ? byLevelMale : byLevelFemale

  const lvl = typeof level === 'number' && Number.isFinite(level) ? Math.floor(level) : 1
  const clamped = Math.max(MIN_CHARACTER_LEVEL, Math.min(MAX_CHARACTER_LEVEL, lvl))

  return pickFromMap(primary, clamped) ?? pickFromMap(fallback, clamped)
}
